/**
 * App.js - 应用根组件，路由配置
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/AuthContext';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';

// 懒加载页面组件
const LoginPage = lazy(() => import('./pages/Login/LoginPage'));
const RegisterPage = lazy(() => import('./pages/Login/RegisterPage'));
const SendPage = lazy(() => import('./pages/Send/SendPage'));
const RulesPage = lazy(() => import('./pages/Rules/RulesPage'));
const CardKeyPage = lazy(() => import('./pages/CardKey/CardKeyPage'));
const LogsPage = lazy(() => import('./pages/Logs/LogsPage'));
const AdminPage = lazy(() => import('./pages/Admin/AdminPage'));
const FeishuAppPage = lazy(() => import('./pages/FeishuApp/FeishuAppPage'));
const WatermarkPage = lazy(() => import('./pages/Watermark/WatermarkPage'));
const ChangePasswordPage = lazy(() => import('./pages/Profile/ChangePasswordPage'));
const NotFoundPage = lazy(() => import('./pages/Login/NotFoundPage'));

/**
 * 受保护路由组件
 */
function ProtectedRoute({ children, admin_only = false }) {
  const { is_authenticated, is_admin, is_card_valid } = useAuth();

  if (!is_authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!is_card_valid && !is_admin) {
    return <Navigate to="/login?expired=1" replace />;
  }

  if (admin_only && !is_admin) {
    return <Navigate to="/send" replace />;
  }

  return children;
}

/**
 * 主路由配置
 */
function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner full />}>
      <Routes>
        {/* 公开路由 */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* 受保护路由 */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/send" replace />} />
          <Route path="send" element={<SendPage />} />
          <Route path="rules" element={<RulesPage />} />
          <Route path="card" element={<CardKeyPage />} />
          <Route path="logs" element={<LogsPage />} />
          <Route path="feishu-app" element={<FeishuAppPage />} />
          <Route path="watermark" element={<WatermarkPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />

          {/* 管理员专属路由 */}
          <Route path="admin/*" element={
            <ProtectedRoute admin_only>
              <AdminPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
